// PHY-05 (front speakers match) — README.md.

import type { Product } from '@roomtreatment/av-catalog';
import type { BuildContext } from '../context';
import type { Issue } from '../types';
import { issueId } from '../copy';

function boundProduct(ctx: BuildContext, channel: string): { id: string; product: Product } | null {
  const id = Object.keys(ctx.channels).find((k) => ctx.channels[k] === channel);
  if (!id) return null;
  const entry = ctx.doc.speakers.find((s) => s.id === id);
  const product = entry?.productId ? ctx.catalog.get(entry.productId) : undefined;
  return product ? { id, product } : null;
}

export function checkPhy05(ctx: BuildContext): Issue[] {
  const fl = boundProduct(ctx, 'FL');
  const fr = boundProduct(ctx, 'FR');
  const c = boundProduct(ctx, 'C');
  if (!fl || !fr || !c) return [];

  const issues: Issue[] = [];

  if (fl.product.id !== fr.product.id) {
    issues.push({
      id: issueId('PHY-05', [fl.id, fr.id]),
      rule: 'PHY-05',
      severity: 'warning',
      text: 'Front left and front right are different models.',
      fix: 'Use the same model for both.',
      subjects: [fl.id, fr.id],
    });
  }

  // A front with no published series is matched by brand, the only family
  // information it has, which is also what the fix below tells the user to
  // do. (This used to demand the exact same product, so a KEF center under
  // series-less KEF fronts was told to "choose a center from KEF".)
  const seriesDiffers = fl.product.series === null
    ? c.product.brand !== fl.product.brand
    : (c.product.brand !== fl.product.brand || c.product.series !== fl.product.series);
  if (seriesDiffers) {
    issues.push({
      id: issueId('PHY-05', [fl.id, c.id]),
      rule: 'PHY-05',
      severity: 'warning',
      text: "The center doesn't match your front speakers.",
      fix: fl.product.series
        ? `Choose a ${fl.product.series} center.`
        : `Choose a center from ${fl.product.brand}.`,
      subjects: [fl.id, c.id],
    });
  }

  return issues;
}
